import React, { useState, useRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import ScreenThumbnail from './ScreenThumbnail';
import ScreenContentSelector from './ScreenContentSelector';
import InterludeModal from './InterludeModal';
import TimerDisplay from './TimerDisplay';
import ModalWrapper from '../ModalWrapper';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$embedded ? '12px' : '24px'};
  padding: ${props => props.$embedded ? '12px' : '24px'};
  background: #0a0a0a;
  min-height: ${props => props.$embedded ? 'auto' : '100%'};
`;

const ScreenGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${props => props.$embedded ? '12px' : '16px'};
  max-width: ${props => props.$embedded ? '100%' : '900px'};
`;

const ScreenItem = styled.div`
  flex: 1 1 calc(25% - 12px);
  min-width: ${props => props.$embedded ? '140px' : '200px'};
  max-width: ${props => props.$embedded ? '160px' : '220px'};
  position: relative;
`;

const ActionsBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #555;
    background: #252525;
  }

  ${props => props.$variant === 'primary' && css`
    background: #0099ff;
    border-color: #0099ff;

    &:hover {
      background: #007acc;
      border-color: #007acc;
    }
  `}

  ${props => props.$variant === 'danger' && css`
    border-color: #ff4444;
    color: #ff4444;

    &:hover {
      background: rgba(255, 68, 68, 0.1);
    }
  `}
`;

const ActionIcon = styled.span`
  font-size: 16px;
`;

const InterlueSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InterlueButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const InterludeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 20px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0099ff;
    background: rgba(0, 153, 255, 0.1);
  }
`;

const ActiveInterludeBanner = styled.div`
  background: linear-gradient(90deg, rgba(0, 153, 255, 0.2), rgba(153, 51, 255, 0.2));
  border: 1px solid rgba(0, 153, 255, 0.3);
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const InterludeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const InterludeTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

const InterludeControls = styled.div`
  display: flex;
  gap: 8px;
`;

const ControlButton = styled.button`
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SelectorPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  z-index: 100;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const ScreenWrapper = styled.div`
  position: relative;
`;

// Modal for expanded screen preview
const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  aspect-ratio: 16 / 9;
  background: #0a0a0a;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #333;
  cursor: default;
`;

const ModalIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ModalHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  z-index: 10;
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalCode = styled.span`
  font-family: 'JetBrains Mono', monospace;
  background: rgba(0, 153, 255, 0.2);
  padding: 4px 10px;
  border-radius: 6px;
  color: #0099ff;
`;

const ModalCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #fff;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const IPadSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #222;
`;

const IPadGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const IPadWrapper = styled.div`
  width: 100px;
  flex-shrink: 0;
  position: relative;
`;

const EmbeddedToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const EmbeddedButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${props => props.$active ? 'rgba(0, 153, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active ? 'rgba(0, 153, 255, 0.4)' : '#333'};
  border-radius: 4px;
  color: ${props => props.$active ? '#0099ff' : '#888'};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(0, 153, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$active ? '#0099ff' : '#fff'};
  }
`;

const EmbeddedDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background: #333;
  margin: 0 8px;
  flex-shrink: 0;
`;

const WallGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 12px;
  margin-right: 12px;
  border-right: 1px solid #333;

  &:last-child {
    border-right: none;
    padding-right: 0;
    margin-right: 0;
  }
`;

const WallGroupLabel = styled.div`
  font-size: 9px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  white-space: nowrap;
`;

const WallGroupScreens = styled.div`
  display: flex;
  gap: 8px;
`;

const SCREEN_CODES = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D1', 'D2'];

const IPAD_CODES = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10'];

const WALLS_CONFIG = [
  { id: 'A', name: 'Pohjoinen', screens: ['A1', 'A2'] },
  { id: 'B', name: 'Itä', screens: ['B1', 'B2'] },
  { id: 'C', name: 'Etelä', screens: ['C1', 'C2'] },
  { id: 'D', name: 'Länsi', screens: ['D1', 'D2'] },
];

const INTERLUDE_TEMPLATES = [
  { id: 'coffee', icon: '☕', label: 'Tauko', type: 'break', title: 'Kahvitauko', minutes: 15 },
  { id: 'countdown', icon: '⏳', label: 'Countdown', type: 'countdown', title: 'Seuraava vaihe', minutes: 1 },
  { id: 'message', icon: '📢', label: 'Viesti', type: 'message', title: '', minutes: 0 },
  { id: 'celebration', icon: '🎉', label: 'Juhla', type: 'celebration', title: 'Kiitos!', minutes: 0 },
];

const VIEW_LABELS = {
  postit: 'PostIt',
  weather: 'Weather',
  'impact-path': 'Impact Path',
  impactpath: 'Impact Path',
  'impact-machine': 'Impact Machine',
  waiting: 'Waiting',
  data: 'Data',
  human: 'Network',
  solutions: 'Solutions',
  futures: 'Futures',
  table: 'Table',
  pivotpoints: 'Pivot Points',
  'pivot-points': 'Pivot Points',
  swot: 'SWOT',
  mood: 'SWOT',
  welcome: 'Welcome',
  kuutio: 'Kuutio',
  timer: 'Timer',
};

// Test mode: each screen shows a different view
const TEST_SCREEN_VIEWS = {
  'A1': 'postit',
  'A2': 'swot',
  'B1': 'impact-path',
  'B2': 'pivotpoints',
  'C1': 'weather',
  'C2': 'swot',
  'D1': 'waiting',
  'D2': 'table',
};

/**
 * ScreenManagerPanel - Main admin panel for managing 8 screens + 10 iPads
 *
 * @param {object} screens - Screen states keyed by code {A1: {...}, A2: {...}, ...}
 * @param {object} ipads - iPad states keyed by code {IPAD1: {...}, IPAD2: {...}, ...}
 * @param {object} activeInterlude - Active interlude state or null
 * @param {function} onScreenSelect - Called when screen is clicked
 * @param {function} onScreenViewChange - Called when view is changed
 * @param {function} oniPadSelect - Called when iPad is clicked
 * @param {function} oniPadViewChange - Called when iPad view is changed
 * @param {function} onInterludeStart - Called to start interlude
 * @param {function} onInterludePause - Called to pause interlude
 * @param {function} onInterludeResume - Called to resume interlude
 * @param {function} onInterludeEnd - Called to end interlude
 * @param {function} onSyncAll - Called to sync all screens
 * @param {function} onSetAllView - Called to set all screens to same view
 */
const ScreenManagerPanel = ({
  screens = {},
  ipads = {},
  activeInterlude = null,
  instanceId = null,
  onScreenSelect,
  onScreenViewChange,
  oniPadSelect,
  oniPadViewChange,
  onInterludeStart,
  onInterludePause,
  onInterludeResume,
  onInterludeEnd,
  onSyncAll,
  onSetAllView,
  embedded = false, // Compact mode for embedding in another panel
  stepScreenConfig = null, // Current flow step's screen config (for test mode sync)
}) => {
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(null);
  const [interludeModalOpen, setInterludeModalOpen] = useState(false);
  const [livePreview, setLivePreview] = useState(embedded); // Auto-enable in embedded mode
  const [testMode, setTestMode] = useState(false); // Don't auto-enable - facilitator chooses
  const [expandedScreen, setExpandedScreen] = useState(null); // Screen code for modal view
  const selectorRef = useRef(null);

  // In embedded mode, show only first iPad for variety
  const EMBEDDED_IPAD_CODES = ['P1'];

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target)) {
        setSelectorOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close expanded modal on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && expandedScreen) {
        setExpandedScreen(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandedScreen]);

  const handleScreenClick = (code) => {
    setSelectedScreen(code);
    setSelectorOpen(selectorOpen === code ? null : code);
    onScreenSelect?.(code);
  };

  const handleViewSelect = (code, view) => {
    onScreenViewChange?.(code, view);
    setSelectorOpen(null);
  };

  const handleQuickInterlude = (template) => {
    onInterludeStart?.({
      type: template.type,
      title: template.title,
      duration: template.minutes * 60,
      showProgress: true,
      showReturnTime: template.type === 'break',
    });
  };

  // Primary screens (left on each wall) vs Secondary screens (right on each wall)
  const PRIMARY_SCREENS = ['A1', 'B1', 'C1', 'D1'];
  const SECONDARY_SCREENS = ['A2', 'B2', 'C2', 'D2'];

  const getScreenState = (code) => {
    // In test mode, simulate all screens online with flow step config
    if (testMode) {
      let view = TEST_SCREEN_VIEWS[code] || 'waiting'; // Default fallback
      let mediaContent = null;

      // Priority 1: Per-screen config (e.g., { A1: {...}, A2: {...} })
      if (stepScreenConfig?.[code]) {
        const screenConfig = stepScreenConfig[code];
        view = screenConfig.view || view;
        mediaContent = screenConfig.params?.mediaContent || null;
      }
      // Priority 2: Dual-screen format (screens.primary / screens.secondary)
      else if (stepScreenConfig?.screens) {
        const isPrimary = PRIMARY_SCREENS.includes(code);
        const screenConfig = isPrimary
          ? stepScreenConfig.screens.primary
          : stepScreenConfig.screens.secondary;
        view = screenConfig?.view || screenConfig?.type || view;
        mediaContent = screenConfig?.params?.mediaContent || null;
      }
      // Priority 3: Legacy single-view format
      else if (stepScreenConfig?.walls?.view) {
        view = stepScreenConfig.walls.view;
      }

      return {
        isOnline: true,
        currentView: view,
        mediaContent,
        preview: {},
        pairingCode: null,
      };
    }
    return screens[code] || {
      isOnline: false,
      currentView: null,
      mediaContent: null,
      preview: {},
      pairingCode: null,
    };
  };

  const getiPadState = (code) => {
    return ipads[code] || {
      isOnline: false,
      currentView: null,
      preview: {},
    };
  };

  // Generate URL for live preview iframe
  // Uses /screen/:code route with ScreenClient for socket events + interlude support
  // virtual=true connects to socket but doesn't register (won't kick out real screens)
  const getLivePreviewUrl = (view, screenCode = null) => {
    if (!instanceId || !view || !screenCode) return null;

    // Route through ScreenClient which handles socket events and interludes
    return `/screen/${screenCode}?instance=${instanceId}&view=${view}&virtual=true`;
  };

  const handleiPadClick = (code) => {
    setSelectedScreen(code);
    setSelectorOpen(selectorOpen === code ? null : code);
    oniPadSelect?.(code);
  };

  const handleiPadViewSelect = (code, view) => {
    oniPadViewChange?.(code, view);
    setSelectorOpen(null);
  };

  return (
    <Container $embedded={embedded}>
      {/* Compact toolbar for embedded mode */}
      {embedded && (
        <EmbeddedToolbar>
          <EmbeddedButton
            $active={testMode}
            onClick={() => setTestMode(!testMode)}
            title={testMode ? 'Näytä oikeat näytöt' : 'Simuloi näyttöjä'}
          >
            {testMode ? '🧪 Testi' : '📺 Live'}
          </EmbeddedButton>
          {testMode && (
            <span style={{ fontSize: 10, color: '#666' }}>
              Simuloidut näytöt - ei oikeita laitteita
            </span>
          )}
        </EmbeddedToolbar>
      )}

      {/* Active Interlude Banner - always show */}
      {activeInterlude && !embedded && (
        <ActiveInterludeBanner>
          <InterludeInfo>
            <InterludeTitle>
              {activeInterlude.title || 'Interlude Active'}
            </InterludeTitle>
            {activeInterlude.remaining !== null && (
              <TimerDisplay
                remaining={activeInterlude.remaining}
                total={activeInterlude.duration}
                size="small"
                showProgress={false}
                isPaused={activeInterlude.isPaused}
              />
            )}
          </InterludeInfo>
          <InterludeControls>
            {activeInterlude.isPaused ? (
              <ControlButton onClick={onInterludeResume}>▶ Resume</ControlButton>
            ) : (
              <ControlButton onClick={onInterludePause}>⏸ Pause</ControlButton>
            )}
            <ControlButton onClick={onInterludeEnd}>⏹ End</ControlButton>
          </InterludeControls>
        </ActiveInterludeBanner>
      )}

      {/* Screen Grid - grouped by wall in embedded mode */}
      {embedded ? (
        <ScreenGrid $livePreview={livePreview} $embedded={embedded}>
          {WALLS_CONFIG.map((wall) => (
            <WallGroup key={wall.id}>
              <WallGroupLabel>{wall.name}</WallGroupLabel>
              <WallGroupScreens>
                {wall.screens.map((code) => {
                  const state = getScreenState(code);
                  return (
                    <ScreenItem key={code} $embedded={embedded}>
                      <ScreenThumbnail
                        code={code}
                        wall={code[0]}
                        isOnline={state.isOnline}
                        currentView={state.currentView}
                        preview={state.preview}
                        pairingCode={state.pairingCode}
                        isSelected={selectedScreen === code}
                        onClick={() => handleScreenClick(code)}
                        onExpand={() => setExpandedScreen(code)}
                        livePreviewUrl={livePreview ? getLivePreviewUrl(state.currentView, code) : null}
                      />
                    </ScreenItem>
                  );
                })}
              </WallGroupScreens>
            </WallGroup>
          ))}
          {/* iPads section */}
          <WallGroup>
            <WallGroupLabel>iPadit</WallGroupLabel>
            <WallGroupScreens>
              {EMBEDDED_IPAD_CODES.map((code) => {
                const state = getiPadState(code);
                return (
                  <ScreenItem key={code} $embedded={embedded}>
                    <ScreenThumbnail
                      code={code}
                      isOnline={testMode || state.isOnline}
                      currentView={testMode ? 'impact-path' : state.currentView}
                      preview={state.preview}
                      isSelected={selectedScreen === code}
                      onClick={() => handleiPadClick(code)}
                      onExpand={() => setExpandedScreen(code)}
                      livePreviewUrl={livePreview ? getLivePreviewUrl(testMode ? 'impact-path' : state.currentView, code) : null}
                    />
                  </ScreenItem>
                );
              })}
            </WallGroupScreens>
          </WallGroup>
        </ScreenGrid>
      ) : (
        <ScreenGrid $livePreview={livePreview} $embedded={embedded}>
          {SCREEN_CODES.map((code) => {
            const state = getScreenState(code);
            return (
              <ScreenItem key={code} ref={selectorOpen === code ? selectorRef : null} $livePreview={livePreview} $embedded={embedded}>
                <ScreenThumbnail
                  code={code}
                  wall={code[0]}
                  isOnline={state.isOnline}
                  currentView={state.currentView}
                  preview={state.preview}
                  pairingCode={state.pairingCode}
                  isSelected={selectedScreen === code}
                  onClick={() => handleScreenClick(code)}
                  onExpand={() => setExpandedScreen(code)}
                  livePreviewUrl={livePreview ? getLivePreviewUrl(state.currentView, code) : null}
                />
                {selectorOpen === code && (
                  <SelectorPopover>
                    <ScreenContentSelector
                      currentView={state.currentView}
                      onSelect={(view) => handleViewSelect(code, view)}
                      showPresentations={true}
                      presentations={[]}
                    />
                  </SelectorPopover>
                )}
              </ScreenItem>
            );
          })}
        </ScreenGrid>
      )}

      {/* iPad Grid - hidden in embedded mode (iPads shown inline above) */}
      {!embedded && (
        <IPadSection>
          <SectionTitle>iPadit</SectionTitle>
          <IPadGrid>
            {IPAD_CODES.map((code) => {
              const state = getiPadState(code);
              return (
                <IPadWrapper key={code} ref={selectorOpen === code ? selectorRef : null}>
                  <ScreenThumbnail
                    code={code}
                    isOnline={state.isOnline}
                    currentView={state.currentView}
                    preview={state.preview}
                    isSelected={selectedScreen === code}
                    onClick={() => handleiPadClick(code)}
                    onExpand={() => setExpandedScreen(code)}
                    compact
                  />
                  {selectorOpen === code && (
                    <SelectorPopover>
                      <ScreenContentSelector
                        currentView={state.currentView}
                        onSelect={(view) => handleiPadViewSelect(code, view)}
                        showPresentations={false}
                        presentations={[]}
                      />
                    </SelectorPopover>
                  )}
                </IPadWrapper>
              );
            })}
          </IPadGrid>
        </IPadSection>
      )}

      {/* Quick Actions - hidden in embedded mode */}
      {!embedded && (
        <ActionsBar>
          <ActionButton
            onClick={() => {
              const newTestMode = !testMode;
              setTestMode(newTestMode);
              // Auto-enable live preview when entering test mode
              if (newTestMode && !livePreview) {
                setLivePreview(true);
              }
            }}
            $variant={testMode ? 'danger' : undefined}
            title={testMode ? 'Exit test mode' : 'Simulate all screens with different views'}
          >
            <ActionIcon>{testMode ? '🧪' : '🔬'}</ActionIcon>
            {testMode ? 'Test Mode ON' : 'Test Mode'}
          </ActionButton>
          <ActionButton
            onClick={() => setLivePreview(!livePreview)}
            $variant={livePreview ? 'primary' : undefined}
            title={livePreview ? 'Switch to status tiles' : 'Show live screen content'}
          >
            <ActionIcon>{livePreview ? '📺' : '👁️'}</ActionIcon>
            {livePreview ? 'Live Preview ON' : 'Live Preview'}
          </ActionButton>
          <ActionButton onClick={onSyncAll}>
            <ActionIcon>🔄</ActionIcon>
            Sync All
          </ActionButton>
          <ActionButton onClick={() => onSetAllView?.('postit')}>
            <ActionIcon>📝</ActionIcon>
            All PostIt
          </ActionButton>
          <ActionButton onClick={() => onSetAllView?.('impact-path')}>
            <ActionIcon>🔗</ActionIcon>
            All Impact Path
          </ActionButton>
          <ActionButton onClick={() => onSetAllView?.('waiting')}>
            <ActionIcon>⏳</ActionIcon>
            All Waiting
          </ActionButton>
        </ActionsBar>
      )}

      {/* Interludes - hidden in embedded mode */}
      {!embedded && (
        <InterlueSection>
          <SectionTitle>Interludes</SectionTitle>
          <InterlueButtons>
            {INTERLUDE_TEMPLATES.map((template) => (
              <InterludeButton
                key={template.id}
                onClick={() => handleQuickInterlude(template)}
              >
                {template.icon} {template.label}
              </InterludeButton>
            ))}
            <InterludeButton onClick={() => setInterludeModalOpen(true)}>
              ⚙️ Custom...
            </InterludeButton>
          </InterlueButtons>
        </InterlueSection>
      )}

      {/* Interlude Modal */}
      <InterludeModal
        visible={interludeModalOpen}
        onClose={() => setInterludeModalOpen(false)}
        onStart={(config) => {
          onInterludeStart?.(config);
          setInterludeModalOpen(false);
        }}
      />

      {/* Expanded Screen Modal */}
      {expandedScreen && (() => {
        const isIPad = expandedScreen.startsWith('P');
        const state = isIPad ? getiPadState(expandedScreen) : getScreenState(expandedScreen);
        const view = state.currentView;
        const previewUrl = getLivePreviewUrl(view, expandedScreen);

        return (
          <ModalWrapper visible={!!expandedScreen} onClose={() => setExpandedScreen(null)} showCloseButton={false} blur={0}>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  <ModalCode>{expandedScreen}</ModalCode>
                  {view && <span>{VIEW_LABELS[view] || view}</span>}
                </ModalTitle>
                <ModalCloseButton onClick={() => setExpandedScreen(null)}>
                  Close (ESC)
                </ModalCloseButton>
              </ModalHeader>
              {previewUrl ? (
                <ModalIframe
                  src={previewUrl}
                  title={`Screen ${expandedScreen} expanded view`}
                />
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#666'
                }}>
                  No view configured
                </div>
              )}
            </ModalContent>
          </ModalWrapper>
        );
      })()}
    </Container>
  );
};

export default ScreenManagerPanel;
